<script setup>
// [TODO] Use Teleport to move the modal to the end of the body
import { ref, computed, watch, nextTick, defineEmits } from 'vue';
import { useEventListener } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';

const { modalType, closeOnBackdropClick, onClose, nome } = defineProps({
  closeOnBackdropClick: { type: Boolean, default: true },
  showCloseButton: { type: Boolean, default: true },
  onClose: { type: Function, default: () => {} },
  fullWidth: { type: Boolean, default: false },
  modalType: { type: String, default: 'centered' },
  size: { type: String, default: '' },
  /**
   * O nome do diálogo para quem não vê a tela. Só é preciso quando o modal
   * não tem título visível — havendo título, ele vira o nome sozinho.
   */
  nome: { type: String, default: '' },
});

// `fechou`: a saída terminou (24/10/2026). Quem desmonta o modal com `v-if` espera por
// este aviso — desmontar no `close` corta a animação de saída no meio.
const emit = defineEmits(['close', 'fechou']);
const show = defineModel('show', { type: Boolean, default: false });

const modalClassName = computed(() => {
  const modalClassNameMap = {
    centered: '',
    'right-aligned': 'right-aligned',
  };

  return `advup-modal-fundo modal-mask skip-context-menu ${
    modalClassNameMap[modalType] || ''
  }`;
});

// [TODO] Revisit this logic to use outside click directive
const mousedDownOnBackdrop = ref(false);

const handleMouseDown = () => {
  mousedDownOnBackdrop.value = true;
};

const close = () => {
  show.value = false;
  emit('close');
  onClose();
};

const onMouseUp = () => {
  if (mousedDownOnBackdrop.value) {
    mousedDownOnBackdrop.value = false;
    if (closeOnBackdropClick) {
      close();
    }
  }
};

// ── O DIÁLOGO COMO DIÁLOGO ─────────────────────────────────────────────────
//
// Medido na tela em 14/09/2026, abrindo "Adicionar card" no quadro: esta casca
// não tinha PAPEL, não tinha NOME e não tinha `aria-modal`; o foco entrava no
// primeiro campo (certo) mas ESCAPAVA — 101 elementos alcançáveis por Tab
// atrás do modal —, e o Escape fechava devolvendo o foco ao `<body>`, não ao
// botão que abriu. São 47 modais só no Pipeline, e esta casca é a de todo
// modal antigo do produto.
//
// Por que não virou `<dialog showModal()>`, que daria as quatro coisas de
// graça (é o que a peça `Dialog` faz): o véu aqui é `.modal-mask`, um
// elemento REAL, e há folha de estilo espalhada pelo produto que depende dele
// e de `.modal-container`. Trocar a estrutura mexeria no desenho de 47 telas
// de uma vez. O que falta é semântica, e semântica dá para acrescentar sem
// mexer em uma linha de desenho.

const containerRef = ref(null);
const tituloId = ref('');
let focoAnterior = null;

/** Só o que o Tab alcança de verdade: visível, habilitado e não `-1`. */
const focaveis = () => {
  if (!containerRef.value) return [];
  return [
    ...containerRef.value.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ),
  ].filter(el => !el.disabled && el.offsetParent !== null);
};

/**
 * O nome vem do título que já está na tela — inventar um segundo texto só
 * para o leitor de tela cria duas verdades sobre o mesmo diálogo.
 */
const nomearPeloTitulo = () => {
  const cx = containerRef.value;
  if (!cx) return;
  const titulo = cx.querySelector('h1, h2, h3, h4, [data-modal-titulo]');
  if (!titulo) {
    tituloId.value = '';
    // Diálogo sem nome é diálogo que o leitor de tela anuncia como "diálogo"
    // e nada mais. Em vez de adivinhar um texto, o caso APARECE: quem abrir
    // este modal em desenvolvimento vê qual é, e resolve pondo um título
    // visível (melhor) ou passando `nome`.
    if (import.meta.env.DEV && !nome) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Modal] sem título e sem `nome`: este diálogo abre sem nome para quem usa leitor de tela.',
        cx
      );
    }
    return;
  }
  if (!titulo.id) {
    titulo.id = `modal-tit-${Math.random().toString(36).slice(2, 9)}`;
  }
  tituloId.value = titulo.id;
};

const aoAbrir = async () => {
  focoAnterior = document.activeElement;
  await nextTick();
  nomearPeloTitulo();
  const alvos = focaveis();
  // O primeiro CAMPO, não o primeiro foco: o primeiro foco costuma ser o X, e
  // abrir um formulário com o dedo no botão de fechar é começar pelo fim.
  const campo = alvos.find(el =>
    ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName)
  );
  (campo || alvos[0] || containerRef.value)?.focus();
};

const aoFechar = () => {
  // Devolver o foco é o que fecha o ciclo para quem navega por teclado: sem
  // isso o Tab seguinte recomeça do topo da página, e a pessoa perde o lugar.
  if (focoAnterior && document.contains(focoAnterior)) focoAnterior.focus();
  focoAnterior = null;
};

watch(show, aberto => (aberto ? aoAbrir() : aoFechar()), { immediate: true });

/** Este é o modal de cima? (modal que abre modal existe no produto.) */
const souOTopo = () => {
  const todos = document.querySelectorAll('.modal-mask');
  return !todos.length || todos[todos.length - 1].contains(containerRef.value);
};

const prenderOTab = e => {
  if (e.code !== 'Tab' || !souOTopo()) return;
  const alvos = focaveis();
  if (!alvos.length) return;
  const primeiro = alvos[0];
  const ultimo = alvos[alvos.length - 1];
  const atual = document.activeElement;
  if (
    e.shiftKey &&
    (atual === primeiro || !containerRef.value.contains(atual))
  ) {
    e.preventDefault();
    ultimo.focus();
  } else if (!e.shiftKey && atual === ultimo) {
    e.preventDefault();
    primeiro.focus();
  }
};

const onKeydown = e => {
  if (!show.value) return;
  if (e.code === 'Escape') {
    close();
    e.stopPropagation();
    return;
  }
  prenderOTab(e);
};

useEventListener(document.body, 'mouseup', onMouseUp);
useEventListener(document, 'keydown', onKeydown);

// Note: onClose prop is deprecated in favor of @close event
// Both patterns work, but @close is preferred
</script>

<template>
  <transition name="modal-fade" @after-leave="emit('fechou')">
    <div
      v-if="show"
      :class="modalClassName"
      transition="modal"
      @mousedown="handleMouseDown"
    >
      <!-- A folha de tela cheia (a galeria de anexos) NÃO veste a peça: ela
           não é um cartão SOBRE a tela, ela É a tela. Por isso mantém o fundo
           translúcido que já tinha — a peça pintaria papel sólido, borda e
           raio de cartão. -->
      <div
        ref="containerRef"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        :aria-labelledby="tituloId || undefined"
        :aria-label="tituloId ? undefined : nome || undefined"
        class="relative max-h-full modal-container rtl:text-right skip-context-menu"
        :class="{
          'advup-modal mx-4': !fullWidth,
          'items-center rounded-none flex h-full justify-center w-full overflow-auto bg-n-alpha-3':
            fullWidth,
          [size]: true,
        }"
        @mouse.stop
        @mousedown="event => event.stopPropagation()"
      >
        <Button
          v-if="showCloseButton"
          ghost
          slate
          icon="i-lucide-x"
          class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
          @click="close"
        />
        <slot />
      </div>
    </div>
  </transition>
</template>

<style lang="scss">
/**
 * A CASCA ANTIGA (`woot-modal`), agora vestindo a PEÇA.
 *
 * Aqui passam os 60 modais que ainda não foram reescritos — 47 só no Pipeline.
 * Até 14/09/2026 este arquivo DESENHAVA o modal: repetia superfície, borda,
 * raio, sombra, véu e animação que `_advup-modal.scss` já define. Peça
 * reescrita é peça que envelhece sozinha — a retirada do filete branco, no
 * mesmo dia, precisou ser feita nos dois lugares.
 *
 * Agora `.modal-mask` É `.advup-modal-fundo` e `.modal-container` É
 * `.advup-modal`. O que sobra aqui é só o que a casca antiga tem de
 * diferente, e cada diferença é uma VARIÁVEL na peça, não uma contra-regra:
 *
 *   --modal-camada   9990, para passar por cima da barra do aplicativo
 *   --modal-respiro  0, porque o véu antigo não tem recuo
 *   --modal-larg     37,5rem, a largura destas 60 telas (a peça usa 42)
 *   --modal-rolagem  auto, porque o conteúdo destas telas não tem corpo com
 *                    rolagem própria como `.advup-modal__corpo`
 *
 * Medido antes e depois nos quatro tamanhos: 600 / 80% / 90% / 95%, os mesmos.
 */
.modal-mask {
  --modal-camada: 9990;
  --modal-respiro: 0;

  .modal-container {
    --modal-larg: 37.5rem;
    --modal-rolagem: auto;
  }

  /* O X mora sobre a faixa colorida quando o modal tem `woot-modal-header`.
     A cor padrao dele e `text-n-slate-12` — quase preto no tema claro, ou
     seja, invisivel sobre o gradiente navy. Sobre a faixa ele fica branco. */
  .modal-container:has(.advup-modal__topo) > button:first-of-type {
    color: #fff;
    background: rgb(255 255 255 / 0.12);
  }

  .modal-container:has(.advup-modal__topo) > button:first-of-type:hover {
    background: rgb(255 255 255 / 0.22);
  }

  .modal-container {
    &.medium {
      @apply max-w-[80%] w-[56.25rem];
    }

    &.large {
      @apply max-w-[90%] w-[72rem] h-[85vh];
    }

    &.full {
      @apply max-w-[95%] w-[90rem] h-[90vh];
    }

    .content {
      @apply p-8;
    }

    form,
    .modal-content {
      @apply pt-4 pb-8 px-8 self-center;

      a {
        @apply p-4;
      }
    }
  }
}

.modal-big {
  @apply w-full;
}

.modal-mask.right-aligned {
  @apply justify-end;

  .modal-container {
    @apply rounded-none h-full w-[30rem];

    /* `margin: auto` da peça CENTRALIZA o item no flex e vence o
       `justify-end` do véu — medido em 14/09/2026: a gaveta parava no meio da
       tela (x=192 numa janela de 864, com 480 de largura). Aqui ela encosta
       na borda, que é o que gaveta faz. */
    margin: 0;
  }
}

/* DEFEITO CORRIGIDO: `.modal-enter` e `.modal-leave` sao os nomes do Vue 2.
   No Vue 3 as classes viraram `-enter-from` / `-leave-to`, entao estas regras
   nunca casaram com nada desde a migracao e TODO modal antigo do produto
   aparecia e sumia seco, sem transicao nenhuma.

   A ENTRADA vem da peça (véu e folha animam separados, cada um com a sua
   física); o que fica aqui é a SAÍDA, que `@keyframes` de entrada não cobre. */
.modal-fade-leave-active {
  transition: opacity var(--mov-2) var(--mov-curva-saida);
}

.modal-fade-leave-to {
  opacity: 0;
}

/* A folha sai pelo caminho por onde chegou (24/10/2026): desce 12px junto com o véu que
   apaga, como a casca da casa (`advup-saida`). Antes só esmaecia no lugar. */
.modal-fade-leave-active .advup-modal {
  transition: transform var(--mov-2) var(--mov-curva-saida);
}

.modal-fade-leave-to .advup-modal {
  transform: translateY(12px) scale(0.985);
}

@media (prefers-reduced-motion: reduce) {
  .modal-fade-leave-active,
  .modal-fade-leave-active .advup-modal {
    transition: none;
  }
}
</style>
